import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import { X } from "lucide-react";
import IconButton from "../Buttons/IconButton";
import { useDialogContext } from "./Dialog";
import DialogDescription from "./DialogDescription";

const useStyles = makeStyles(() => ({
  header: {
    display: "flex",
    flexDirection: "column",
    padding: "16px 16px 8px 16px",
    gap: "4px",
  },
  headerTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "16px",
  },
  headerContent: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minWidth: 0,
  },
}));

/**
 * Dialog header section. Renders its children (typically a DialogTitle and
 * optionally a DialogDescription) with an optional close button.
 *
 * If a DialogDescription is present among children it is rendered below the
 * title/close-button row and stretches to the full available width.
 *
 * @param {boolean}  closeable  - Whether to show the X close button — default true
 * @param {function} onClose    - Override close handler (falls back to Dialog's onClose)
 * @param {string}   className  - Additional class name
 */
const DialogHeader = ({ closeable = true, onClose: onCloseProp, className = "", children }) => {
  const classes = useStyles();
  const { onClose: contextOnClose } = useDialogContext();
  const handleClose = onCloseProp || contextOnClose;

  const childArray = React.Children.toArray(children);
  const descriptionChildren = childArray.filter(
    (child) => React.isValidElement(child) && child.type === DialogDescription
  );
  const otherChildren = childArray.filter(
    (child) => !(React.isValidElement(child) && child.type === DialogDescription)
  );

  return (
    <div className={`${classes.header} ${className}`}>
      <div className={classes.headerTop}>
        <div className={classes.headerContent}>{otherChildren}</div>
        {closeable && handleClose && (
          <IconButton onClick={handleClose} size="small" ariaLabel="Close dialog">
            <X />
          </IconButton>
        )}
      </div>
      {descriptionChildren.length > 0 && <div>{descriptionChildren}</div>}
    </div>
  );
};

export default DialogHeader;
